import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { DateTime } from "luxon";
import { BehaviorSubject, of } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { hashCode } from "../../../lib/lib";
import { RequestsFilterDto } from "../../../lib/types";
import { RequestsService } from "../../api";
import {
  SelectTimeRangeComponent,
  SelectTimeRangeResult
} from "../logs-list/select-time-range/select-time-range.component";
import {
  SelectZombieActionComponent,
  ZombieActionResult
} from "../logs-list/select-zombie-action/select-zombie-action.component";
import { RequestsFilterComponent } from "../requests-filter/requests-filter.component";

@Component({
  selector: "app-requests-list",
  templateUrl: "./requests-list.component.html",
  styleUrls: ["./requests-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestsListComponent implements OnInit, OnChanges {
  @Input() zombieId?: number;
  @Input() dateStart?: string;
  @Input() dateEnd?: string;
  @Input() requestLike?: string;
  @Input() takeMs?: string;
  @Input() error?: boolean;
  @Input() limit: number = 100;

  @ViewChild(RequestsFilterComponent)
  filterComp!: RequestsFilterComponent;

  initFilterParams: RequestsFilterDto = { limit: 100 };
  @Output() filter = new BehaviorSubject<RequestsFilterDto>(this.initFilterParams);
  loading = true;

  list$ = this.filter.pipe(
    tap(() => (this.loading = true)),
    switchMap(data => {
      return this.api.list(
        data.zombieId,
        data.dateStart,
        data.dateEnd,
        data.method,
        data.requestLike,
        data.takeMs,
        data.limit,
        data.error as any
      );
    }),
    catchError(err => {
      if (err instanceof HttpErrorResponse) {
        this.snackBar.open(JSON.stringify(err.error), "ok");
      } else {
        this.snackBar.open(err.message, "ok");
      }
      return of([]);
    }),
    tap(() => (this.loading = false))
  );

  constructor(
    private api: RequestsService,
    private snackBar: MatSnackBar,
    private matBottomSheet: MatBottomSheet,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initFilterParams = {
      limit: this.limit,
      zombieId: this.zombieId,
      dateStart: this.dateStart,
      dateEnd: this.dateEnd,
      requestLike: this.requestLike,
      takeMs: this.takeMs,
      error: this.error
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.filter.next({
      limit: this.limit,
      zombieId: this.zombieId,
      dateStart: this.dateStart,
      dateEnd: this.dateEnd,
      requestLike: this.requestLike,
      takeMs: this.takeMs,
      error: this.error
    });
  }

  update(ev: any) {
    let data = {
      ...ev,
      dateStart: ev.dateStart instanceof Date ? ev.dateStart.toISOString() : ev.dateStart,
      dateEnd: ev.dateEnd instanceof Date ? ev.dateEnd.toISOString() : ev.dateEnd
    };
    this.filter.next(data);
  }

  selectZombie(zombieId: number) {
    this.matBottomSheet
      .open<SelectZombieActionComponent, any, ZombieActionResult>(SelectZombieActionComponent)
      .afterDismissed()
      .subscribe(rs => {
        if (rs === ZombieActionResult.FILTER) {
          this.filterComp.form.patchValue({ zombieId });
        }
        if (rs === ZombieActionResult.OPEN_ZOMBIE_PAGE) {
          const url = this.router.serializeUrl(this.router.createUrlTree(["/zombies/z", zombieId]));
          window.open(url, "_blank");
        }
        if (rs === ZombieActionResult.OPEN_ZOMBIE_LOGS_PAGE) {
          const url = this.router.serializeUrl(
            this.router.createUrlTree(["/logs"], {
              queryParams: {
                ...this.filter.getValue(),
                zombieId
              }
            })
          );
          window.open(url, "_blank");
        }
      });
  }

  selectTimeRange(item: any) {
    console.log(item);
    this.matBottomSheet
      .open<SelectTimeRangeComponent, any, SelectTimeRangeResult>(SelectTimeRangeComponent)
      .afterDismissed()
      .subscribe(rs => {
        let queryParams = null;
        if (rs === SelectTimeRangeResult.BEFORE) {
          queryParams = {
            zombieId: item.zombie_id,
            dateStart: DateTime.fromISO(item.ts)
              .minus({ minutes: 5 })
              .toUTC()
              .toISO(),
            dateEnd: item.ts
          };
        }

        if (rs === SelectTimeRangeResult.AFTER) {
          queryParams = {
            zombieId: item.zombie_id,
            dateEnd: DateTime.fromISO(item.ts)
              .plus({ minutes: 1 })
              .toUTC()
              .toISO(),
            dateStart: item.ts
          };
        }

        if (rs === SelectTimeRangeResult.AROUND) {
          queryParams = {
            zombieId: item.zombie_id,
            dateStart: DateTime.fromISO(item.ts)
              .minus({ minutes: 1 })
              .toUTC()
              .toISO(),
            dateEnd: DateTime.fromISO(item.ts)
              .plus({ minutes: 1 })
              .toUTC()
              .toISO()
          };
        }

        if (queryParams) {
          const hc = hashCode(item.ts + item.message);
          const url = this.router.serializeUrl(
            this.router.createUrlTree(["/logs"], {
              queryParams: {
                ...queryParams,
                highlight: hc
              }
            })
          );
          console.log(url, queryParams);
          window.open(url, "_blank");
        }
      });
  }
}
